import { useQuery } from '@tanstack/react-query'
import { Edit, Gift, Trash } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  createPremio,
  deletePremio,
  getAllPremios,
  getPremioById,
  type ICreatePremio,
  type IPremio,
  updatePremio,
} from '@/api/campaigns/premio'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { PremioModal } from './premioModal'

export function PremioCRUD({ campanhaId }: { campanhaId: number }) {
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentPremio, setCurrentPremio] = useState<IPremio | null>(null)

  const { data: premios, refetch } = useQuery({
    queryKey: ['premios', campanhaId],
    queryFn: () => getAllPremios(campanhaId),
  })

  interface Premio {
    id: number
    descricao: string
    posicao: number
  }

  const filteredPremios = premios?.filter((premio: Premio) =>
    premio.descricao.toLowerCase().includes(search.toLowerCase()),
  )

  const handleCreateOrUpdate = async (data: ICreatePremio) => {
    try {
      if (currentPremio) {
        await updatePremio(currentPremio.id, data)
        toast.success('Prêmio atualizado com sucesso!')
      } else {
        await createPremio(data)
        toast.success('Prêmio criado com sucesso!')
      }
      refetch()
      setIsModalOpen(false)
      setCurrentPremio(null)
    } catch (error) {
      toast.error('Erro ao salvar prêmio')
    }
  }

  const handleEdit = async (id: number) => {
    const premio = await getPremioById(id)
    setCurrentPremio(premio)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deletePremio(id)
      toast.success('Prêmio excluído com sucesso!')
      refetch()
    } catch (error) {
      toast.error('Erro ao excluir prêmio')
    }
  }

  return (
    <div className="space-y-4">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-md">
          <Input
            placeholder="Buscar prêmios..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="w-full sm:w-auto"
        >
          <Gift className="mr-2 h-4 w-4" />
          Adicionar Prêmio
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <div className="min-w-[600px] sm:min-w-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50%] text-center">Descrição</TableHead>
                <TableHead className="w-[30%] text-center">Posição</TableHead>
                <TableHead className="w-[20%] text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPremios?.length ? (
                filteredPremios.map((premio: Premio) => (
                  <TableRow key={premio.id}>
                    <TableCell className="text-center font-medium">
                      {premio.descricao}
                    </TableCell>
                    <TableCell className="text-center">
                      {premio.posicao}º lugar
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="grid justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(premio.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(premio.id)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    Nenhum prêmio encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <PremioModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleCreateOrUpdate}
        premio={currentPremio}
      />
    </div>
  )
}
